/**
 * Utility functions for file handling
 */

import { getAccessToken } from '../services/authStorage';

/**
 * Append the current access token as a `token=` query param to a SAME-ORIGIN
 * API URL. The FITS preview/download-proxy endpoints now require authentication
 * (requireAuthOrToken), but they're consumed via <img src>, <a> navigation and
 * header-less fetch — none of which can send an Authorization header — so the
 * token must travel in the URL. Never attached to presigned third-party URLs
 * (e.g. Backblaze B2), which carry their own signature and must not receive our
 * token.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
export const withAuthToken = (url) => {
  if (!url) return url;
  if (!url.includes('/api/')) return url; // presigned/third-party — leave alone
  const token = getAccessToken();
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
};

/**
 * Construct full URL from various path formats
 * Handles:
 * 1. Full URLs (http://..., https://...) - returns as-is (presigned URLs)
 * 2. API proxy paths (/api/...) - adds protocol and hostname
 * 3. Storage relative paths (thumbnails/..., previews/...) - uses API proxy endpoint
 *
 * Works for both:
 * - Development: Local MinIO (proxied through API)
 * - Production: Backblaze B2 (presigned URLs or API proxy)
 *
 * @param {string} path - Path in any of the above formats
 * @param {string|number|Date} [version] - Optional cache-bust token. Typically
 *   a UserFile's `updated_at`. When provided, appended as `?v=<token>` so a
 *   regenerated preview at the same S3 key (e.g. after Rebuild Previews)
 *   isn't served from the browser's long-lived cache.
 * @returns {string|null} - Full URL or null if no path provided
 */
export const constructMinioUrl = (path, version, sigParams) => {
  if (!path) return null;

  const appendVersion = (url) => {
    if (!version) return url;
    const token = version instanceof Date ? version.getTime() : version;
    return url.includes('?') ? `${url}&v=${encodeURIComponent(token)}` : `${url}?v=${encodeURIComponent(token)}`;
  };

  // #1287 / #1306 §E: for an authenticated proxy URL, prefer the server-issued signed-URL
  // params (exp/sig) over the rotating access token. The signature is STABLE across token
  // refreshes, so the browser cache + the route's ETag/304 keep working — no full-grid
  // re-download storm. Falls back to the token when the server didn't sign (rollout/skew safe).
  const appendAuth = (url) => {
    if (sigParams && sigParams.sig && sigParams.exp != null) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}exp=${encodeURIComponent(sigParams.exp)}&sig=${encodeURIComponent(sigParams.sig)}`;
    }
    return withAuthToken(url);
  };

  // Already a full URL (presigned URL from server) — carries its own auth.
  if (path.startsWith('http')) return appendVersion(path);

  // API proxy path - add protocol and hostname. These hit our authenticated proxy. Do NOT
  // attach a preview signature here: the sig was computed over the STORAGE-RELATIVE preview
  // key, which is not the tail of an arbitrary /api/ path — attaching it would fail
  // verification. Use the token (the pre-#1287 behavior) for these.
  if (path.startsWith('/api/')) {
    const protocol = window.location.protocol;
    const host = window.location.host; // includes port
    return withAuthToken(appendVersion(`${protocol}//${host}${path}`));
  }

  // Storage relative path - use API proxy endpoint (authenticated preview). This is what the
  // sig was signed over, so appendAuth may use exp/sig here. Works for local MinIO and B2.
  const protocol = window.location.protocol;
  const host = window.location.host;
  // Encode each path segment individually to preserve slashes for Express routing
  const encodedPath = path.split('/').map(segment => encodeURIComponent(segment)).join('/');
  return appendAuth(appendVersion(`${protocol}//${host}/api/v1/fits/preview/${encodedPath}`));
};

/**
 * Build a cache-stable preview/thumbnail URL for a FILE object (#1287). Single choke point so
 * every grid/detail consumer benefits: prefers the server-issued signature (preview_sig/exp),
 * falling back to the token when unsigned. INVARIANT: emit a sig iff the server returned one —
 * never gate on a client build flag — so GUI/API deploy skew is safe both directions.
 *
 * @param {object} file - a normalized/raw file object with preview_url + preview_sig/preview_exp.
 * @param {{thumbnail?: boolean}} [opts] - build the thumbnail URL instead of the preview.
 */
export const filePreviewUrl = (file, opts = {}) => {
  if (!file) return null;
  const cacheBust = file.preview_updated_at || file.updated_at;
  if (opts.thumbnail) {
    return constructMinioUrl(file.thumbnail_url, cacheBust, { sig: file.thumbnail_sig, exp: file.thumbnail_exp });
  }
  const key = file.preview_url || file.thumbnail_url;
  const sig = file.preview_url
    ? { sig: file.preview_sig, exp: file.preview_exp }
    : { sig: file.thumbnail_sig, exp: file.thumbnail_exp };
  return constructMinioUrl(key, cacheBust, sig);
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};
